import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Pagination,
  Alert,
  Toolbar,
  Tooltip,
  SelectChangeEvent,
} from "@mui/material";
// DatePicker removed due to compatibility issues with date-fns v3
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { useTranslation } from "react-i18next";
import { useAuthInfo } from "@propelauth/react";
import { useAxiosInstance } from "src/api/axiosInstance";
import { format } from "date-fns";
import FusePageSimple from "@fuse/core/FusePageSimple";
import FuseLoading from "@fuse/core/FuseLoading";
import {
  getNotificationsAdmin,
  sendManualNotification,
} from "src/api/notifications";
import { showMessage } from "@fuse/core/FuseMessage/fuseMessageSlice";
import { useAppDispatch } from "app/store/hooks";

interface NotificationFilters {
  orgId: string;
  userId: string;
  notificationType: string;
  languageSent: string;
  seen: string;
  startDate: Date | null;
  endDate: Date | null;
}

interface Notification {
  id: number;
  org_id: string;
  org_name: string;
  user_id: string;
  user_name: string;
  notification_type: string;
  content: string;
  language_sent: string;
  timestamp: string;
  seen: boolean;
  seen_at?: string;
  metadata?: any;
}

function NotificationsAdminApp() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user, accessToken } = useAuthInfo();
  const axiosInstance = useAxiosInstance();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filters, setFilters] = useState<NotificationFilters>({
    orgId: "",
    userId: "",
    notificationType: "",
    languageSent: "",
    seen: "",
    startDate: null,
    endDate: null,
  });

  const [sendForm, setSendForm] = useState({
    recipients: "all",
    notificationType: "manual",
    title: "",
    body: "",
    metadata: {},
  });

  // For PropelAuth, we need to use useOrg hook to get organization/role info
  // Since the user object doesn't contain org info, let's try a backend API call
  const [isGodfather, setIsGodfather] = useState(false);
  const [roleCheckLoading, setRoleCheckLoading] = useState(true);

  // Check role via backend API call
  useEffect(() => {
    const checkRole = async () => {
      if (!accessToken) return;

      try {
        // Make a simple API call that requires Godfather role using axiosInstance
        // If it succeeds, user has Godfather role
        const instance = await axiosInstance;
        const response = await instance.get(
          "/notifications/admin/notifications?limit=1"
        );

        console.log("🔍 Role check response status:", response.status);

        if (response.status === 200) {
          setIsGodfather(true);
          console.log("✅ User has Godfather role");
        } else {
          console.log("⚠️ Unexpected response status:", response.status);
          setIsGodfather(false);
        }
      } catch (error: any) {
        console.log("🔍 Role check error:", error.response?.status);

        if (error.response?.status === 403) {
          setIsGodfather(false);
          console.log("❌ User does not have Godfather role");
        } else {
          console.error("Error checking role:", error);
          setIsGodfather(false);
        }
      } finally {
        setRoleCheckLoading(false);
      }
    };

    checkRole();
  }, [accessToken]);

  useEffect(() => {
    if (roleCheckLoading) return; // Wait for role check to complete

    if (!isGodfather) {
      dispatch(
        showMessage({
          message: "Unauthorized access",
          variant: "error",
        })
      );
      return;
    }

    fetchNotifications();
  }, [isGodfather, roleCheckLoading, page, filters]);

  const fetchNotifications = async () => {
    if (!accessToken) return;

    setLoading(true);
    try {
      const params = {
        ...filters,
        // Convert seen string to boolean for API call
        seen: filters.seen === "" ? undefined : filters.seen === "true",
        startDate: filters.startDate
          ? format(filters.startDate, "yyyy-MM-dd")
          : undefined,
        endDate: filters.endDate
          ? format(filters.endDate, "yyyy-MM-dd")
          : undefined,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      };

      const response = await getNotificationsAdmin(axiosInstance, params);
      setNotifications(response.notifications);
      setTotalCount(response.total);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      dispatch(
        showMessage({
          message: "Failed to fetch notifications",
          variant: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof NotificationFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(1);
  };

  const handleSendNotification = async () => {
    if (!accessToken) return;

    try {
      // Convert recipients based on the selected value
      let recipients;
      if (sendForm.recipients === "all") {
        recipients = "all";
      } else if (sendForm.recipients.startsWith("role:")) {
        const role = sendForm.recipients.split(":")[1];
        recipients = { role };
      } else if (sendForm.recipients.startsWith("org:")) {
        const orgId = sendForm.recipients.split(":")[1];
        recipients = { orgId };
      } else {
        // Handle array of user IDs if needed
        recipients = sendForm.recipients.split(",");
      }

      const notificationData = {
        ...sendForm,
        recipients,
      };

      await sendManualNotification(notificationData, axiosInstance);
      setSendDialogOpen(false);
      setSendForm({
        recipients: "all",
        notificationType: "manual",
        title: "",
        body: "",
        metadata: {},
      });

      dispatch(
        showMessage({
          message: "Notification sent successfully",
          variant: "success",
        })
      );

      // Refresh notifications list
      fetchNotifications();
    } catch (error) {
      console.error("Error sending notification:", error);
      dispatch(
        showMessage({
          message: "Failed to send notification",
          variant: "error",
        })
      );
    }
  };

  const getStatusChip = (seen: boolean) => {
    return (
      <Chip
        label={seen ? t("SEEN") : t("UNSEEN")}
        color={seen ? "success" : "warning"}
        size="small"
        icon={
          seen ? (
            <FuseSvgIcon>heroicons-outline:check</FuseSvgIcon>
          ) : (
            <FuseSvgIcon>heroicons-outline:x</FuseSvgIcon>
          )
        }
      />
    );
  };

  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case "room_assigned":
        return "primary";
      case "room_reassigned":
        return "secondary";
      case "maintenance_problem":
        return "error";
      case "manual":
        return "info";
      default:
        return "default";
    }
  };

  if (roleCheckLoading) {
    return <FuseLoading />;
  }

  if (!isGodfather) {
    return (
      <FusePageSimple
        header={
          <div className="flex flex-col sm:flex-row space-y-16 sm:space-y-0 flex-1 w-full items-center justify-between py-32 px-24 md:px-32">
            <Typography
              component="h1"
              className="text-3xl md:text-4xl font-extrabold tracking-tight leading-7 sm:leading-10 truncate"
            >
              {t("UNAUTHORIZED_ACCESS")}
            </Typography>
          </div>
        }
        content={
          <div className="w-full p-24">
            <Alert severity="error">{t("ADMIN_ACCESS_REQUIRED")}</Alert>
          </div>
        }
      />
    );
  }

  return (
    <FusePageSimple
      header={
        <div className="flex flex-col sm:flex-row space-y-16 sm:space-y-0 flex-1 w-full items-center justify-between py-32 px-24 md:px-32">
          <Typography
            component="h1"
            className="text-3xl md:text-4xl font-extrabold tracking-tight leading-7 sm:leading-10 truncate"
          >
            {t("NOTIFICATIONS_ADMIN")}
          </Typography>
          <div className="flex space-x-8">
            <Button
              variant="contained"
              color="primary"
              startIcon={
                <FuseSvgIcon>heroicons-outline:paper-airplane</FuseSvgIcon>
              }
              onClick={() => setSendDialogOpen(true)}
            >
              {t("SEND_NOTIFICATION")}
            </Button>
            <Tooltip title={t("REFRESH")}>
              <IconButton onClick={fetchNotifications}>
                <FuseSvgIcon>heroicons-outline:refresh</FuseSvgIcon>
              </IconButton>
            </Tooltip>
          </div>
        </div>
      }
      content={
        <div className="w-full p-24">
          {loading && <FuseLoading />}

          <Card className="mb-24">
            <CardContent>
              <Toolbar className="px-0">
                <Typography variant="h6" className="flex-1">
                  {t("FILTERS")}
                </Typography>
                <Button
                  startIcon={
                    <FuseSvgIcon>heroicons-outline:funnel</FuseSvgIcon>
                  }
                  onClick={() => setFiltersOpen(!filtersOpen)}
                >
                  {filtersOpen ? t("HIDE_FILTERS") : t("SHOW_FILTERS")}
                </Button>
              </Toolbar>

              {filtersOpen && (
                <Grid container spacing={2} className="mt-16">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label={t("ORGANIZATION_ID")}
                      value={filters.orgId}
                      onChange={(e) =>
                        handleFilterChange("orgId", e.target.value)
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label={t("USER_ID")}
                      value={filters.userId}
                      onChange={(e) =>
                        handleFilterChange("userId", e.target.value)
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t("NOTIFICATION_TYPE")}</InputLabel>
                      <Select
                        value={filters.notificationType}
                        onChange={(e) =>
                          handleFilterChange("notificationType", e.target.value)
                        }
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="room_assigned">Room Assigned</MenuItem>
                        <MenuItem value="room_reassigned">
                          Room Reassigned
                        </MenuItem>
                        <MenuItem value="maintenance_problem">
                          Maintenance Problem
                        </MenuItem>
                        <MenuItem value="manual">Manual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t("LANGUAGE")}</InputLabel>
                      <Select
                        value={filters.languageSent}
                        onChange={(e) =>
                          handleFilterChange("languageSent", e.target.value)
                        }
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="bg">Bulgarian</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t("STATUS")}</InputLabel>
                      <Select
                        value={filters.seen}
                        onChange={(e) =>
                          handleFilterChange("seen", e.target.value)
                        }
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Seen</MenuItem>
                        <MenuItem value="false">Unseen</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label={t("START_DATE")}
                      value={
                        filters.startDate
                          ? format(filters.startDate, "yyyy-MM-dd")
                          : ""
                      }
                      onChange={(e) =>
                        handleFilterChange(
                          "startDate",
                          e.target.value ? new Date(e.target.value) : null
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label={t("END_DATE")}
                      value={
                        filters.endDate
                          ? format(filters.endDate, "yyyy-MM-dd")
                          : ""
                      }
                      onChange={(e) =>
                        handleFilterChange(
                          "endDate",
                          e.target.value ? new Date(e.target.value) : null
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-16">
                {t("NOTIFICATIONS")} ({totalCount})
              </Typography>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("CLIENT_NAME")}</TableCell>
                      <TableCell>{t("USER_NAME")}</TableCell>
                      <TableCell>{t("TYPE")}</TableCell>
                      <TableCell>{t("CONTENT")}</TableCell>
                      <TableCell>{t("LANGUAGE")}</TableCell>
                      <TableCell>{t("TIME_SENT")}</TableCell>
                      <TableCell>{t("STATUS")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.map((notification) => (
                      <TableRow key={notification.id}>
                        <TableCell>{notification.org_name}</TableCell>
                        <TableCell>{notification.user_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={notification.notification_type}
                            color={
                              getNotificationTypeColor(
                                notification.notification_type
                              ) as any
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {notification.content}
                          </Typography>
                        </TableCell>
                        <TableCell>{notification.language_sent}</TableCell>
                        <TableCell>
                          {format(new Date(notification.timestamp), "PPpp")}
                        </TableCell>
                        <TableCell>
                          {getStatusChip(notification.seen)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalCount > rowsPerPage && (
                <Box className="flex justify-center mt-16">
                  <Pagination
                    count={Math.ceil(totalCount / rowsPerPage)}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Send Notification Dialog */}
          <Dialog
            open={sendDialogOpen}
            onClose={() => setSendDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>{t("SEND_NOTIFICATION")}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} className="mt-8">
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t("RECIPIENTS")}</InputLabel>
                    <Select
                      value={sendForm.recipients}
                      onChange={(e) =>
                        setSendForm((prev) => ({
                          ...prev,
                          recipients: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="all">All Users</MenuItem>
                      <MenuItem value="role:cleaner">All Cleaners</MenuItem>
                      <MenuItem value="role:tech">All Tech Support</MenuItem>
                      <MenuItem value="role:admin">All Admins</MenuItem>
                      <MenuItem value="role:reception">All Reception</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("NOTIFICATION_TYPE")}
                    value={sendForm.notificationType}
                    onChange={(e) =>
                      setSendForm((prev) => ({
                        ...prev,
                        notificationType: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("TITLE")}
                    value={sendForm.title}
                    onChange={(e) =>
                      setSendForm((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={t("BODY")}
                    value={sendForm.body}
                    onChange={(e) =>
                      setSendForm((prev) => ({ ...prev, body: e.target.value }))
                    }
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSendDialogOpen(false)}>
                {t("CANCEL")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSendNotification}
                disabled={!sendForm.title || !sendForm.body}
                startIcon={
                  <FuseSvgIcon>heroicons-outline:paper-airplane</FuseSvgIcon>
                }
              >
                {t("SEND")}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      }
    />
  );
}

export default NotificationsAdminApp;
